/**
 * The table an editor asks DuckDB about when it needs a report's columns.
 *
 * It has to be the table the ENGINE queries, and
 * `report_engine.discovery.build_report_from_dict` takes that from the
 * FIRST declared source (`primary.sql_fragment or
 * report_config["duckdb_table"]`): a declared source always wins, and
 * the bare `duckdb_table` field only speaks for a report without one.
 * Until 2026-09-11 the pickers read that field first — and the create
 * modal used to fill it with the report KEY, so on staging
 * `unauthorized_monthly` introspected a table called
 * `unauthorized_monthly` while its data lives in the view
 * `unauthorized_listings`. Reports created before that still carry it.
 *
 * This is a MIRROR of `builder/service.py::_derive_primary_table` plus
 * the legacy-field step that follows it in `validate_config`: the table
 * the editor shows is the table publish validates. Both sides run the
 * same cases from `apps/ai-reporting-builder/tests/fixtures/
 * primary_table_contract.json` — change one without the other and one
 * of the two suites fails.
 *
 * Only kinds whose primary reference is a plain table name derive
 * (`view` with a `target`, `duckdb_table`). A source whose reference is
 * not a table name at all — a parquet glob, an attached file — answers
 * with the legacy field, as the validator does; the fixture carries what
 * the engine queries in those cases, so the gap stays written down.
 *
 * A `view` WITHOUT a `target` is the exception: it answers "" and not
 * the legacy field. The engine names that view itself (`{name}_data`),
 * the legacy field names something else, and showing the admin the
 * columns of the wrong table is the bug this file exists to close.
 * Publish refuses the same shape, so the editor showing no table is the
 * honest answer. `defaultViewTarget` is what the Data tab writes so the
 * shape doesn't happen in the first place.
 *
 * Returns "" when nothing resolves. Callers treat that as "no table",
 * never as a name.
 */

import type { ReportConfig } from "@/types";

/** A string, trimmed; anything else is not a table name. */
function tableNameOf(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** `sources[0]` when the list has entries, else the legacy singular
 *  `source` — the pick discovery makes. */
function primarySourceOf(report: ReportConfig): unknown {
  const sources = report.sources;
  if (Array.isArray(sources) && sources.length > 0) return sources[0];
  return report.source;
}

/**
 * The table a view source materializes into when the admin doesn't name
 * one — the same name the engine derives (`source_report_key` +
 * `{report_key}_data` in `report_engine/source.py`). The Data tab writes
 * it into `target` on create, so the config says it out loud instead of
 * three layers deriving it separately.
 */
export function defaultViewTarget(sourceName: string): string {
  return `${sourceName.trim()}_data`;
}

export function resolvePrimaryTable(report: ReportConfig): string {
  const primary = primarySourceOf(report);
  if (typeof primary === "object" && primary !== null && !Array.isArray(primary)) {
    const source = primary as Record<string, unknown>;
    if (source.kind === "view") {
      // No target: "no table", never the legacy field (see the header).
      return tableNameOf(source.target);
    }
    if (source.kind === "duckdb_table") {
      const table = tableNameOf(source.table);
      if (table !== "") return table;
    }
  }
  return tableNameOf(report.duckdb_table);
}
